import { Card } from "@/components/ui/card";
import { Calendar, TrendingUp } from "lucide-react";
import { format, startOfMonth } from "date-fns";
import { useLanguage } from "@/contexts/LanguageContext";

interface Message {
  id: string;
  timestamp: Date | number;
}

interface TimelineProps {
  messages: Message[];
  onDateClick?: (messageId: string) => void;
}
// Helper to normalize any timestamp type to milliseconds
function toMillis(ts: any): number {
  if (!ts) return 0;

  if (ts instanceof Date) return ts.getTime();

  if (typeof ts === "number") {
    // if your timestamps are in seconds, use: return ts * 1000;
    return ts;
  }

  if (typeof ts === "string") {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  }

  // Firestore Timestamp object
  if (typeof ts.toDate === "function") {
    return ts.toDate().getTime();
  }

  // Firestore plain object { seconds, nanoseconds }
  if (typeof ts.seconds === "number") {
    const { seconds, nanoseconds = 0 } = ts;
    return seconds * 1000 + Math.floor(nanoseconds / 1e6);
  }

  return 0;
}

const Timeline = ({ messages, onDateClick }: TimelineProps) => {
  const { t } = useLanguage();

  // Group messages by month and find first message of each month
  const calculateActivity = () => {
    if (messages.length === 0) return [];

    // Filter out messages without valid timestamps and sort
    const validMessages = messages.filter((msg) => msg.timestamp !== null && msg.timestamp !== undefined);

    const sortedMessages = [...validMessages].sort((a, b) => {
      const timeA = toMillis(a.timestamp);
      const timeB = toMillis(b.timestamp);
      return timeA - timeB;
    });

    // Group by month and collect first message ID of each month
    const monthGroups = new Map<string, { count: number; date: Date; firstMessageId: string }>();

    sortedMessages.forEach((msg) => {
      const timestamp = typeof msg.timestamp === "number" ? new Date(msg.timestamp) : (msg.timestamp as Date);
      const monthStart = startOfMonth(timestamp);
      const monthKey = monthStart.getTime().toString();

      if (!monthGroups.has(monthKey)) {
        monthGroups.set(monthKey, {
          count: 1,
          date: monthStart,
          firstMessageId: msg.id,
        });
      } else {
        const group = monthGroups.get(monthKey)!;
        group.count++;
      }
    });

    // Convert to array and sort by date (most recent first)
    const activity = Array.from(monthGroups.values())
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, 12); // Show last 12 months with activity

    return activity;
  };

  const timelineEvents = calculateActivity();
  const maxCount = Math.max(...timelineEvents.map((e) => e.count), 1);

  const getActivityLabel = (count: number) => {
    const ratio = count / maxCount;
    if (ratio >= 0.8) return t("timeline.peak");
    if (ratio >= 0.5) return t("timeline.high");
    if (ratio >= 0.3) return t("timeline.regular");
    return t("timeline.light");
  };

  if (timelineEvents.length === 0) {
    return (
      <Card className="p-4 sm:p-6 shadow-soft">
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          <h3 className="text-base sm:text-lg font-bold">{t("timeline.title")}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t("timeline.noData")}</p>
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6 shadow-soft">
      <div className="flex items-center gap-2 mb-3 sm:mb-4">
        <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
        <h3 className="text-base sm:text-lg font-bold">{t("timeline.title")}</h3>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {timelineEvents.map((event, index) => (
          <div
            key={index}
            className="relative pl-7 sm:pl-8 pb-3 sm:pb-4 last:pb-0 cursor-pointer transition-smooth hover:bg-muted/50 rounded-lg p-2 -ml-2"
            onClick={() => onDateClick?.(event.firstMessageId)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onDateClick?.(event.firstMessageId);
              }
            }}
          >
            <div className="absolute left-0 top-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full gradient-primary flex items-center justify-center shadow-elegant">
              <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
            </div>
            {index < timelineEvents.length - 1 && (
              <div className="absolute left-2.5 sm:left-3 top-6 sm:top-7 w-0.5 h-full bg-border"></div>
            )}
            <div>
              <p className="font-semibold text-xs sm:text-sm">{format(event.date, "MMMM yyyy")}</p>
              <p className="text-xs text-muted-foreground">{getActivityLabel(event.count)}</p>
              <div className="mt-1.5 sm:mt-2 flex items-center gap-2">
                <div className="h-2 bg-secondary rounded-full flex-1">
                  <div
                    className="h-full gradient-primary rounded-full"
                    style={{ width: `${(event.count / maxCount) * 100}%` }}
                  ></div>
                </div>
                <span className="text-xs font-medium">{event.count}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default Timeline;
